import { Icon } from "@iconify/react/dist/iconify.js";
import { Box, ButtonBase, FormHelperText, Grid, Stack } from "@mui/material";
import moment from "moment";
import React, { useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import CustomSelect from "../../../components/Form/CustomSelect";
import CustomTextField from "../../../components/Form/CustomTextField";
import { getCoeBiddingList } from "../../../store/actions";
import BindingResult from "../modal/BindingResult";

const newPricingTypeExplanations = [
    {
        value: 1,
        title: 'OTR Price',
        description: 'Price shown is indicative and based on the latest COE results. It is automatically updated after each COE exercise. Final pricing may vary. Insurance is not included.',
    },
    {
        value: 2,
        title: 'Downpayment',
        description: 'The downpayment and monthly instalment shown are fixed and will not be affected by future COE price changes.',
    },
    {
        value: 3,
        title: 'Promotion Price',
        description: 'Includes a valid, in-stock COE and will not be affected by future COE price changes.',
    },
];

const PricingInfo = ({ formObj, handleFormChange, handlePricingTypeDataChange, vehicle, windowWidth, isSingPass, setVehicleDetailsOpen,
    advertType, isDirectSeller, error, bodyList, license_categories, advertInterestRate, addAdvert, setNotification, coeList }) => {
    const [bindingResultOpen, setBindingResultOpen] = React.useState(false);
    const [interestRateOptions, setInterestRateOptions] = React.useState([]);
    const dispatch = useDispatch();

    const currentPriceType = formObj?.price_type;
    const priceTypeData = formObj?.price_type_data?.[currentPriceType] || {};
    const selectedPricingTypeExplanation = newPricingTypeExplanations.find(
        (item) => item.value === Number(currentPriceType)
    );
    const handlePTDChange = (field, value) => {
        if (!currentPriceType) return;
        handlePricingTypeDataChange(currentPriceType, field, value);
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    const newPriceTypeList = [
        { label: 'OTR Price', value: 1 },
        { label: 'Downpayment', value: 2 },
        { label: 'Promotion Price', value: 3 },
    ]

    const usedPriceTypeList = [
        { label: 'Selling Price', value: 4 },
        { label: 'Downpayment', value: 2 },
        { label: 'Promotion Price', value: 3 },
    ]
    const directSellerPriceTypeList = [
        { label: 'Selling Price', value: 4 },
        // { label: 'Promotion Price', value: 3 },
    ]

    useEffect(() => {
        if (!advertInterestRate?.library || !vehicle) return;

        const licenseCategoryName = license_categories?.find((category) => category.id === vehicle?.license_category_id)?.name || '';
        const licenseCategoryInterestRate = advertInterestRate?.library[licenseCategoryName] || null;
        const interestRateList = licenseCategoryInterestRate?.filter((item) => item.bike_advert_type === advertType) || [];
        setInterestRateOptions(interestRateList);
    }, [advertInterestRate, formObj?.bike_advert_type, advertType, license_categories])

    useEffect(() => {
        dispatch(getCoeBiddingList());
    }, [])

    useEffect(() => {
        if (coeList?.data?.length > 0) {
            const num = Number((coeList?.data?.[0]?.premium || "0").toString().replace(/,/g, ""));

            handleFormChange('coe', num?.toFixed(2));
        }
    }, [coeList])

    useEffect(() => { //calculate term charge then monthly instalment using fresh term charge value
        const foundOption = interestRateOptions?.find((option) => option.id === priceTypeData?.interest_rate_id);
        const rawInterest = foundOption?.interest_rate;
        const interest_rate = (rawInterest === "NaN" || rawInterest == null) ? 0 : Number(rawInterest);
        const termCharge = (Number(priceTypeData?.loan_amount || 0) * (Number(priceTypeData?.max_loan_tenure || 0) / 12) * interest_rate / 100).toFixed(2);

        const totalPayable = Number(priceTypeData?.loan_amount || 0) + parseFloat(termCharge);
        const tenure = Number(priceTypeData?.max_loan_tenure || 0);
        const monthlyInstalment = totalPayable / tenure;

        handlePTDChange('term_charge', termCharge);
        handlePTDChange('monthly_instalment', isFinite(monthlyInstalment) && monthlyInstalment > 0 ? Math.ceil(monthlyInstalment).toFixed(2) : "0.00");
    }, [interestRateOptions, priceTypeData?.loan_amount, priceTypeData?.interest_rate_id, priceTypeData?.max_loan_tenure])

    useEffect(() => { //calculate loan amount
        if (!currentPriceType) return;
        const type = currentPriceType === 1 ? 'machine_price' : currentPriceType === 2 ? "downpayment" : currentPriceType === 3 ? 'promotion_price' : currentPriceType === 4 ? 'selling_price' : null;
        handleChangePrice(type);
    }, [formObj?.machine_price, currentPriceType, formObj?.promotion_price, formObj?.selling_price, priceTypeData?.min_downpayment, formObj?.coe])

    const handleChangePrice = (field) => {
        const minDownpayment = Number(priceTypeData?.min_downpayment || 0);

        if (currentPriceType === 1) {
            const loanAmount = (Number(formObj?.machine_price || 0) + Number(formObj?.coe || 0) + Number(formObj?.additional_costs || 0) - minDownpayment).toFixed(2);
            handlePTDChange('loan_amount', loanAmount);
        } else if (currentPriceType !== 1 && currentPriceType !== 2) {
            const loanAmount = (Number(formObj?.[field] || 0) - minDownpayment).toFixed(2);
            handlePTDChange('loan_amount', loanAmount);
        }
    }//end calculate loan amount


    useEffect(() => {
        const type = formObj?.price_type ? formObj?.price_type : advertType === 'new' ? 1 : advertType === 'used' ? 4 : null;
        handleFormChange('price_type', type);
    }, [advertType])

    const priceTypeList = isDirectSeller ? directSellerPriceTypeList : (advertType === 'used' ? usedPriceTypeList : newPriceTypeList);

    const availablePriceTypes = isDirectSeller ? [4, 3] : (advertType === 'used' ? [4, 2, 3] : [1, 2, 3]);
    const currentPriceTypeIndex = availablePriceTypes.indexOf(currentPriceType);
    const pricingError = (field) => currentPriceTypeIndex !== -1
        ? error?.['pricings.' + currentPriceTypeIndex + '.' + field] || error?.[field]
        : error?.[field];

    return (
        <React.Fragment>
            <Box className="vehicle-info-container">
                <div className="vehicle-selected-absolute">
                    Selected
                </div>
                <div className="text-semibold font-size-18 pt-2">{vehicle?.make || '-'} {vehicle?.model || ''} {vehicle?.manu_year ? (`(${vehicle?.manu_year})`) : ''}</div>
                <Box sx={{ display: { lg: 'flex', md: 'flex' }, justifyContent: { lg: 'space-between', md: 'space-between' } }}>
                    <Stack sx={{ flexDirection: { lg: 'row', md: 'row', sm: 'column', xs: 'column' }, gap: 2, mt: 2 }}>
                        <div className="d-flex align-items-center">
                            <div className="subheader-2">Reg. No:</div>
                            <div className="mx-1">{vehicle?.reg_no || '-'}</div>
                        </div>
                        {!isSingPass ?
                            <>
                                <div className="d-flex align-items-center">
                                    <div className="subheader-2">Stock ID:</div>
                                    <div className="mx-1">{vehicle?.stock_id || '-'}</div>
                                </div> <div className="d-flex align-items-center">
                                    <div className="subheader-2">Body Type:</div>
                                    <div className="mx-1">{Array.isArray(vehicle?.body_type) && vehicle?.body_type.length > 0 ? vehicle.body_type?.map((item, idx) => bodyList?.find((body) => body.id === item)?.name).join(', ') : vehicle?.body_type || '-'}</div>
                                </div>
                            </> :
                            <>
                                <div className="d-flex align-items-center">
                                    <div className="subheader-2">COE Expiry Date:</div>
                                    <div className="mx-1">{vehicle?.coe_expiry ? moment(vehicle?.coe_expiry, "YYYY-MM-DD").format('DD-MM-YYYY') : '-'}</div>
                                </div> <div className="d-flex align-items-center">
                                    <div className="subheader-2">No. of Owner(s):</div>
                                    <div className="mx-1">{vehicle?.no_of_owner || '0'}</div>
                                </div>
                            </>
                        }
                    </Stack>
                    <div className="d-flex align-items-end">
                        <ButtonBase onClick={() => setVehicleDetailsOpen(true)} style={{ borderRadius: 10, paddingInline: 5 }}>
                            <div className="d-flex align-items-center" style={{ color: '#B48A3C' }}>View details <Icon icon={'fluent:chevron-right-20-regular'} style={{ marginLeft: 5, height: 16 }} /></div>
                        </ButtonBase>
                    </div>
                </Box>
            </Box>
            {
                advertType === 'rental' && !isDirectSeller ?
                    <Box mt={4}>
                        <div className="text-semibold mb-2">Rental Rate</div>
                        <div className="subheader-2 mb-2">Please enter at least one rental rate (daily, 3-day, weekly, or monthly) before continuing.</div>
                        {error && error?.pricing_type && <FormHelperText className='d-flex align-items-center m-0 mt-1' sx={{ color: "#BA2727" }}><Icon icon={'fluent:error-circle-24-filled'} height={14} width={14} />{error?.pricing_type || ""}</FormHelperText>}
                        <Grid container spacing={2}>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                                <CustomTextField
                                    title={'Daily Rate'}
                                    placeholder={'Daily Rate'}
                                    type={"number"}
                                    is2dp={true}
                                    values={formObj?.daily_rate}
                                    onChange={(e) => handleFormChange('daily_rate', e)}
                                    InputProps={{ startAdornment: '$' }}
                                    error={error?.daily_rate}
                                />
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                                <CustomTextField
                                    title={'Weekend Rate'}
                                    placeholder={'Weekend Rate'}
                                    type={"number"}
                                    is2dp={true}
                                    values={formObj?.weekend_rate}
                                    onChange={(e) => handleFormChange('weekend_rate', e)}
                                    InputProps={{ startAdornment: '$' }}
                                    error={error?.weekend_rate}
                                />
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                                <CustomTextField
                                    title={'3-day Rate'}
                                    placeholder={'3-day Rate'}
                                    type={"number"}
                                    is2dp={true}
                                    values={formObj?.three_day_rate}
                                    onChange={(e) => handleFormChange('three_day_rate', e)}
                                    InputProps={{ startAdornment: '$' }}
                                    error={error?.['3_days_rate']}
                                />
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                                <CustomTextField
                                    title={'Weekly Rate'}
                                    placeholder={'Weekly Rate'}
                                    type={"number"}
                                    is2dp={true}
                                    values={formObj?.weekly_rate}
                                    onChange={(e) => handleFormChange('weekly_rate', e)}
                                    InputProps={{ startAdornment: '$' }}
                                    error={error?.weekly_rate}
                                />
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                                <CustomTextField
                                    title={'Monthly Rate'}
                                    placeholder={'Monthly Rate'}
                                    type={"number"}
                                    is2dp={true}
                                    values={formObj?.monthly_rate}
                                    onChange={(e) => handleFormChange('monthly_rate', e)}
                                    InputProps={{ startAdornment: '$' }}
                                    error={error?.monthly_rate}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    :
                    <>
                        {!isDirectSeller && <Box mt={4}>
                            <div className="text-semibold">Price to be shown on ad listing</div>
                            <Stack sx={{ flexDirection: 'row', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                                {priceTypeList.map((price, index) => (
                                    <div
                                        className={formObj?.price_type === price.value ? "vehicle-small-button-selected" : "vehicle-small-button"}
                                        onClick={() => {
                                            handleFormChange('price_type', price.value);
                                        }}
                                    >
                                        {price.label}
                                    </div>
                                ))}
                            </Stack>
                            {error?.pricing_type && <FormHelperText className='d-flex align-items-center m-0 mt-1' sx={{ color: "#BA2727" }}><Icon icon={'fluent:error-circle-24-filled'} height={14} width={14} />{error?.pricing_type || ""}</FormHelperText>}
                            {advertType?.toLowerCase() === 'new' && selectedPricingTypeExplanation && (
                                <Box mt={2}>
                                    {/* <div className="text-semibold font-size-14">{selectedPricingTypeExplanation.title}</div> */}
                                    <div className="subheader-2 mt-1">{selectedPricingTypeExplanation.description}</div>
                                </Box>
                            )}
                        </Box>}
                        {
                            isDirectSeller ?
                                <Box mt={4}>
                                    <div className="text-semibold">Define how much you’re selling this motorcycle for</div>
                                    <Grid container spacing={2}>
                                        <Grid item lg={6} md={6} sm={12} xs={12}>
                                            <CustomTextField
                                                title={formObj?.price_type === 3 ? 'Promotion Price' : 'Selling Price'}
                                                placeholder={formObj?.price_type === 3 ? 'Promotion Price' : 'Selling Price'}
                                                type={'number'}
                                                is2dp={true}
                                                values={(formObj?.price_type === 3 ? priceTypeData?.promotion_price : priceTypeData?.selling_price)}
                                                onChange={(e) => handlePTDChange(formObj?.price_type === 3 ? 'promotion_price' : 'selling_price', e)}
                                                InputProps={{ startAdornment: '$' }}
                                                error={formObj?.price_type === 3 ? pricingError('promotion_price') : pricingError('selling_price')}
                                            />
                                        </Grid>
                                        {/* {
                                            formObj?.price_type === 3 &&
                                            <Grid item lg={6} md={6} sm={12} xs={12}>
                                                <CustomTextField
                                                    title={'Selling Price'}
                                                    placeholder={'Selling Price'}
                                                    type={'number'}
                                                    is2dp={true}
                                                    values={priceTypeData?.selling_price}
                                                    onChange={(e) => handlePTDChange('selling_price', e)}
                                                    InputProps={{ startAdornment: '$' }}
                                                    error={pricingError('selling_price')}
                                                />
                                            </Grid>
                                        } */}
                                    </Grid>
                                </Box> :
                                <>
                                    <Box mt={4}>
                                        <div className="text-semibold">Pricing Details</div>
                                        <Grid container spacing={2}>
                                            <Grid item lg={6} md={6} sm={12} xs={12}>
                                                <CustomTextField
                                                    title={'Purchase Cost'}
                                                    placeholder={'Purchase Cost'}
                                                    type={'number'}
                                                    is2dp={true}
                                                    values={formObj?.purchase_price || '0.00'}
                                                    onChange={(e) => handleFormChange('purchase_price', e)}
                                                    InputProps={{ startAdornment: '$' }}
                                                    disabled={true}
                                                />
                                            </Grid>
                                            {advertType?.toLowerCase() !== "used" &&
                                                <>
                                                    <Grid item lg={6} md={6} sm={12} xs={12}>
                                                        <CustomTextField
                                                            title={'ARF'}
                                                            placeholder={'ARF'}
                                                            type={'number'}
                                                            is2dp={true}
                                                            values={formObj?.arf || '0.00'}
                                                            onChange={(e) => handleFormChange('arf', e)}
                                                            InputProps={{ startAdornment: '$' }}
                                                            disabled={true}
                                                        />
                                                    </Grid>
                                                    <Grid item lg={6} md={6} sm={12} xs={12}>
                                                        <CustomTextField
                                                            title={'Purchase Cost + ARF'}
                                                            placeholder={'Purchase Cost + ARF'}
                                                            type={'number'}
                                                            is2dp={true}
                                                            disabled={true}
                                                            values={Number(Number(formObj?.purchase_price) || 0 + Number(formObj?.arf) || 0).toFixed(2) || '0.00'}
                                                            // onChange={(e) => handleFormChange('purchase_price', e)}
                                                            InputProps={{ startAdornment: '$' }}
                                                        />
                                                    </Grid>
                                                </>}
                                            <Grid item lg={6} md={6} sm={12} xs={12}>
                                                <CustomTextField
                                                    title={'Recommended Selling Price'}
                                                    placeholder={'Recommended Selling Price'}
                                                    type={'number'}
                                                    is2dp={true}
                                                    values={formObj?.suggested_price || '0.00'}
                                                    onChange={(e) => handleFormChange('suggested_price', e)}
                                                    InputProps={{ startAdornment: '$' }}
                                                    disabled={true}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Box mt={4}>
                                        <div className="text-semibold">Define how much you’re selling this motorcycle for</div>
                                        <Grid container spacing={2}>
                                            {
                                                formObj?.price_type === 1 ?
                                                    <>
                                                        <Grid item lg={6} md={6} sm={12} xs={12}>
                                                            <CustomTextField
                                                                title="Machine Price ($)"
                                                                placeholder="Machine Price ($)"
                                                                values={formObj?.machine_price}
                                                                onChange={(e) => {
                                                                    handleFormChange('machine_price', e)
                                                                }}
                                                                InputProps={{ startAdornment: '$' }}
                                                                type={'number'}
                                                                is2dp={true}
                                                                error={pricingError('machine_price')}
                                                            />
                                                        </Grid>
                                                        {advertType === "new" &&
                                                            <Grid item lg={6} md={6} sm={12} xs={12}>
                                                                <CustomTextField
                                                                    title={'COE ($)'}
                                                                    placeholder={'COE ($)'}
                                                                    values={formObj?.coe}
                                                                    // onChange={(e) => handleFormChange('coe', e)}
                                                                    type={'number'}
                                                                    is2dp={true}
                                                                    disabled={true}
                                                                    InputProps={{
                                                                        startAdornment: '$',
                                                                        endAdornment:
                                                                            <ButtonBase className="mt-2" onClick={() => { setBindingResultOpen(true) }}>
                                                                                <Icon icon={'ri:question-line'} style={{ fontSize: 20, color: '#808588' }} />
                                                                            </ButtonBase>
                                                                    }}
                                                                    error={pricingError('coe')}
                                                                />
                                                            </Grid>}

                                                        <Grid item lg={6} md={6} sm={12} xs={12}>
                                                            <CustomTextField
                                                                title={'Additional Cost ($)'}
                                                                values={formObj?.additional_costs}
                                                                onChange={(e) => {
                                                                    handleFormChange('additional_costs', e)
                                                                }}
                                                                InputProps={{ startAdornment: '$' }}
                                                                type={'number'}
                                                                is2dp={true}
                                                                error={pricingError('additional_costs')}
                                                            />
                                                        </Grid>

                                                        <Grid item lg={6} md={6} sm={12} xs={12}>
                                                            <CustomTextField
                                                                title={'Price to be Advertised ($)'}
                                                                placeholder={'Price to be Advertised ($)'}
                                                                values={Number(Number(formObj?.machine_price || 0) + Number(formObj?.coe || 0) + Number(formObj?.additional_costs || 0)).toFixed(2) || 0}
                                                                // onChange={(e) => {
                                                                //     handleFormChange('price_to_be_advertised', e)
                                                                // }}
                                                                disabled={true}
                                                                InputProps={{ startAdornment: '$' }}
                                                                type={'number'}
                                                                is2dp={true}
                                                                error={pricingError('price_to_be_advertised')}
                                                            />
                                                        </Grid>
                                                    </> : formObj?.price_type === 3 ?
                                                        <>
                                                            {/* <Grid item lg={6} md={6} sm={12} xs={12}>
                                                                <CustomTextField
                                                                    title={advertType === 'new' ? 'Machine Price' : 'Selling Price'}
                                                                    placeholder={advertType === 'new' ? 'Machine Price' : 'Selling Price'}
                                                                    values={advertType === 'new' ? formObj?.machine_price  : formObj?.selling_price }
                                                                    onChange={(e) => handleFormChange(advertType === 'new' ? 'machine_price' : 'selling_price', e)}
                                                                    InputProps={{ startAdornment: '$' }}
                                                                    type={'number'}
is2dp={true}
                                                                    error={advertType === 'new' ? error?.machine_price : error?.selling_price}
                                                                />
                                                            </Grid> */}
                                                            <Grid item lg={6} md={6} sm={12} xs={12}>
                                                                <CustomTextField
                                                                    title={'Promotion Price ($)'}
                                                                    placeholder={'Promotion Price ($)'}
                                                                    values={formObj?.promotion_price}
                                                                    onChange={(e) => handleFormChange('promotion_price', e)}
                                                                    type={'number'}
                                                                    is2dp={true}
                                                                    InputProps={{ startAdornment: '$' }}
                                                                    error={pricingError('promotion_price')}
                                                                />
                                                            </Grid>
                                                        </> : formObj?.price_type === 4 ?
                                                            <>
                                                                <Grid item lg={6} md={6} sm={12} xs={12}>
                                                                    <CustomTextField
                                                                        title={'Selling Price ($)'}
                                                                        placeholder={'Selling Price ($)'}
                                                                        values={formObj?.selling_price}
                                                                        onChange={(e) => handleFormChange('selling_price', e)}
                                                                        type={'number'}
                                                                        is2dp={true}
                                                                        InputProps={{ startAdornment: '$' }}
                                                                        error={pricingError('selling_price')}
                                                                    />
                                                                </Grid>
                                                            </>
                                                            : <>
                                                                {/* <Grid item lg={6} md={6} sm={12} xs={12}>
                                                                    <CustomTextField
                                                                        title={advertType === 'new' ? 'Machine Price' : 'Selling Price'}
                                                                        placeholder={advertType === 'new' ? 'Machine Price' : 'Selling Price'}
                                                                        values={advertType === 'new' ? formObj?.machine_price  : formObj?.selling_price }
                                                                        onChange={(e) => handleFormChange(advertType === 'new' ? 'machine_price' : 'selling_price', e)}
                                                                        InputProps={{ startAdornment: '$' }}
                                                                        type={'number'}
is2dp={true}
                                                                        error={advertType === 'new' ? error?.machine_price : error?.selling_price}
                                                                    />
                                                                </Grid> */}
                                                            </>
                                            }

                                            <Grid item lg={6} md={6} sm={12} xs={12}>
                                                <CustomTextField
                                                    title={'Min. Downpayment ($)'}
                                                    placeholder={'Min. Downpayment ($)'}
                                                    values={priceTypeData?.min_downpayment}
                                                    onChange={(e) => handlePTDChange('min_downpayment', e)}
                                                    type={'number'}
                                                    is2dp={true}
                                                    InputProps={{ startAdornment: '$' }}
                                                    error={pricingError('min_downpayment')}
                                                />
                                            </Grid>
                                            {formObj?.price_type === 2 &&
                                                <Grid item lg={6} md={6} sm={12} xs={12}>
                                                    <CustomTextField
                                                        title={'Estimated Loan Amount ($)'}
                                                        placeholder={'Estimated Loan Amount ($)'}
                                                        values={priceTypeData?.loan_amount}
                                                        onChange={(e) => handlePTDChange('loan_amount', e)}
                                                        type={'number'}
                                                        is2dp={true}
                                                        InputProps={{ startAdornment: '$' }}
                                                        error={pricingError('loan_amount')}
                                                    />
                                                </Grid>
                                            }
                                            {formObj?.price_type !== 2 &&
                                                <Grid item lg={6} md={6} sm={12} xs={12}>
                                                    <CustomTextField
                                                        title={'Loan Amount ($)'}
                                                        placeholder={'Loan Amount ($)'}
                                                        values={priceTypeData?.loan_amount}
                                                        onChange={(e) => handlePTDChange('loan_amount', e)}
                                                        type={'number'}
                                                        is2dp={true}
                                                        disabled={true}
                                                        InputProps={{ startAdornment: '$' }}
                                                        error={pricingError('loan_amount')}
                                                    />
                                                </Grid>}
                                            <Grid item lg={6} md={6} sm={12} xs={12}>
                                                <CustomTextField
                                                    title={'Max. Loan Tenure (Months)'}
                                                    // placeholder={'Max. Loan Tenure (Months)'}
                                                    values={priceTypeData?.max_loan_tenure ?? ""}
                                                    type={'integer'}
                                                    max={formObj?.max_loan_tenure_calculated}
                                                    subtitle={"Max. Loan Tenure cannot exceed " + formObj?.max_loan_tenure_calculated + "."}
                                                    onChange={(e) => { handlePTDChange('max_loan_tenure', e); }}
                                                    // InputProps={{ startAdornment: '$' }}
                                                    error={pricingError('max_loan_tenure')}
                                                />
                                            </Grid>
                                            <Grid item lg={6} md={6} sm={12} xs={12}>
                                                <CustomSelect
                                                    key={currentPriceType}
                                                    title={'Interest Rate (%)'}
                                                    placeholder={'Interest Rate (%)'}
                                                    values={priceTypeData?.interest_rate_id ?? ""}
                                                    data={interestRateOptions}
                                                    labelName={'interest_rate'}
                                                    valueName={'id'}
                                                    onChange={(e) => handlePTDChange('interest_rate_id', e)}
                                                    error={pricingError('interest_rate_id')}
                                                />
                                            </Grid>
                                            <Grid item lg={6} md={6} sm={12} xs={12}>
                                                <CustomTextField
                                                    title={'Term Charge ($)'}
                                                    placeholder={'Term Charge ($)'}
                                                    values={priceTypeData?.term_charge}
                                                    type={'number'}
                                                    is2dp={true}
                                                    disabled={true}
                                                    onChange={(e) => handlePTDChange('term_charge', e)}
                                                    InputProps={{ startAdornment: '$' }}
                                                    error={pricingError('term_charge')}
                                                />
                                            </Grid>
                                            <Grid item lg={6} md={6} sm={12} xs={12}>
                                                <CustomTextField
                                                    title={'Monthly Instalment ($)'}
                                                    placeholder={'Monthly Instalment ($)'}
                                                    values={priceTypeData?.monthly_instalment || "0.00"}
                                                    type={'number'}
                                                    is2dp={true}
                                                    disabled={true}
                                                    onChange={(e) => handlePTDChange('monthly_instalment', e)}
                                                    InputProps={{ startAdornment: '$' }}
                                                    error={pricingError('monthly_instalment')}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </>
                        }
                    </>
            }
            <BindingResult
                open={bindingResultOpen}
                close={() => setBindingResultOpen(false)}
                windowWidth={windowWidth}
                resultData={coeList?.data || []}
            />
        </React.Fragment>
    )
}

function mapStateToProps({ General, Advert }) {
    return {
        bodyList: General.bike_types,
        license_categories: General.license_categories,
        advertInterestRate: General.advertInterestRate,
        addAdvert: Advert.advertAdd,
        coeList: Advert.coeBiddingList,

    };
}

export default connect(mapStateToProps)(PricingInfo);
